import { useGroupGuid } from '../components/IAP/hooks/commonHooks';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import apiConfig from '../constants/apiConfig';
import { AORHierarchyPayload } from '../components/MapPage/CorporateViewTabs/AorsTab/utils/schema';
import { useEpaFlag, useUscgFocRequirementsFlag } from 'utils/featureFlags';
import { queryFemaRegionAorsByHierarchy } from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/queryFemaRegionAors';

const keys = {
  base: 'aorsByHierarchy',
  list: (groupGuid: string | undefined) =>
    [keys.base, groupGuid ?? ''] as const,
} as const;

const getAorsByHierarchy = async (groupGuid: string) => {
  const response = await axios.get<AORHierarchyPayload>(
    apiConfig.apiGateway.getAorsByHierarchy(groupGuid)
  );
  return response.data;
};

/**
 * AOR hierarchy for the current organization. The EPA flag replaces the saved
 * AORs with FEMA regions. USCG FOC Requirements reads saved AORs, which
 * `useEnsureUscgFocAors` has replaced with the Coast Guard hierarchy, and wins
 * over EPA when both are on so the tour targets stay valid.
 */
export const useGetAorsByHierarchy = () => {
  const groupGuid = useGroupGuid();
  const epaEnabled = useEpaFlag();
  const uscgFocEnabled = useUscgFocRequirementsFlag();
  const source = !uscgFocEnabled && epaEnabled ? 'epa' : 'db';
  return useQuery({
    queryKey: [...keys.list(groupGuid), source],
    queryFn: async (): Promise<AORHierarchyPayload> => {
      if (source === 'epa' && groupGuid)
        return queryFemaRegionAorsByHierarchy(groupGuid);
      return getAorsByHierarchy(groupGuid);
    },
    enabled: Boolean(groupGuid),
  });
};
